import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { AiAgent } from 'src/app/models/AiAgent';
import { AiModelCatalogEntry } from 'src/app/models/AiModelPrice';
import { AgentDraft, AiAgentService } from 'src/app/shared/services/ai-agent.service';
import { AiModelPriceService } from 'src/app/shared/services/ai-model-price.service';
import { WhatsappAccount } from 'src/app/shared/services/whatsapp-accounts.service';

/** Sufixo da cópia, como o Bruno pediu: "<nome> (cópia)". O nome segue editável. */
export const COPY_SUFFIX = ' (cópia)';

/** Mesmo limite do backend para o nome da cópia. */
export const AGENT_NAME_MAX = 100;

/** uuid v4 para o rascunho; `crypto.randomUUID` não existe em navegador antigo nem fora de https. */
export function newDraftKey(): string {
  const cryptoApi: any = (globalThis as any).crypto;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

/**
 * Formulário curto de agente (Decisão 2 do protótipo): nome, modelo e prompt, com a conversa de
 * teste ao lado (CU-17tm466mg5z, Bruno 09/10). Usado em dois lugares:
 *
 * - **Criar um agente novo** no passo "Responder com IA" do WhatsApp: nasce ativo e ligado ao
 *   número do passo, e volta selecionado.
 * - **Copiar agente** (`source`), no passo e em Meus Agentes: abre com os dados do original
 *   ("<nome> (cópia)"), editáveis, e SÓ cria ao confirmar — Cancelar não cria nada. Eventos e
 *   ofertas vêm do original no backend; listas não.
 *
 * O teste roda antes de criar (rascunho, pelo `draftKey`); ao criar, o backend passa o teste e o
 * gasto dele para o agente novo. O formulário completo continua em IA → Meus Agentes.
 */
@Component({
  selector: 'app-quick-agent-modal',
  templateUrl: './quick-agent-modal.component.html',
  styleUrls: ['./quick-agent-modal.component.scss'],
})
export class QuickAgentModalComponent implements OnInit, OnDestroy {
  /** Número do passo de WhatsApp (criar). Na cópia vale o número do original. */
  @Input() account: WhatsappAccount | null = null;
  /** Agente copiado. Nulo = criar do zero. */
  @Input() source: AiAgent | null = null;
  /**
   * `step`: atalho do passo de WhatsApp (o agente volta selecionado, então nasce ativo).
   * `agents`: Meus Agentes (o cliente escolhe se a cópia já nasce ativa).
   */
  @Input() context: 'step' | 'agents' = 'step';

  name = '';
  modelId = '';
  prompt = '';
  active = true;
  catalog: AiModelCatalogEntry[] = [];
  attempted = false;
  saving = false;
  error: string | null = null;

  /** Rascunho do teste. Um por formulário aberto. */
  readonly draftKey = newDraftKey();

  private subscriptions: Subscription[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private aiAgentService: AiAgentService,
    private aiModelPriceService: AiModelPriceService,
  ) {}

  ngOnInit(): void {
    if (this.source) {
      this.name = this.copyName(this.source.name);
      this.prompt = this.source.base_prompt || '';
      this.modelId = this.source.smsfunnel_ai_model_id || '';
    }

    this.subscriptions.push(
      this.aiModelPriceService.catalog().subscribe({
        next: models => {
          this.catalog = models || [];
          // Na cópia, um modelo que saiu do catálogo não é trocado por outro sem o cliente ver:
          // o select fica vazio e a cópia mantém o do original.
          if (!this.source && !this.modelId && this.catalog.length) {
            this.modelId = this.catalog[0].id;
          }
          if (this.modelId && !this.catalog.some(m => m.id === this.modelId)) {
            this.modelId = '';
          }
        },
        error: () => this.error = 'Erro ao carregar os modelos de IA.',
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isCopy(): boolean {
    return !!this.source;
  }

  get modelDescription(): string {
    return this.catalog.find(m => m.id === this.modelId)?.display_description || '';
  }

  /** O número por onde o agente vai responder: o do passo (criar) ou o do original (cópia). */
  get accountName(): string {
    if (this.isCopy) {
      return this.source?.whatsapp_account?.name || '';
    }
    return this.account?.name || '';
  }

  /** Na cópia o modelo pode ficar vazio: vale o do original. */
  get valid(): boolean {
    return !!this.name.trim() && (this.isCopy || !!this.modelId) && !!this.prompt.trim();
  }

  /** O que a conversa de teste usa: o rascunho como está no formulário agora. */
  get draft(): AgentDraft {
    return {
      name: this.name.trim(),
      base_prompt: this.prompt,
      smsfunnel_ai_model_id: this.modelId,
      source_agent_id: this.source?.id || null,
      draft_key: this.draftKey,
    };
  }

  get draftHint(): string {
    if (!this.modelId && !this.prompt.trim()) {
      return 'Escolha o modelo e escreva o prompt para testar.';
    }
    return !this.modelId ? 'Escolha o modelo de IA para testar.' : 'Escreva o prompt base para testar.';
  }

  /** No passo de WhatsApp o agente volta selecionado: precisa nascer ativo. */
  get canChooseActive(): boolean {
    return this.context === 'agents';
  }

  save(): void {
    this.attempted = true;
    if (!this.valid) {
      return;
    }

    this.saving = true;
    this.error = null;
    const active = this.canChooseActive ? this.active : true;

    const request$ = this.isCopy
      ? this.aiAgentService.duplicate(this.source.id, {
        name: this.name.trim(),
        base_prompt: this.prompt.trim(),
        active,
        ...(this.modelId ? { smsfunnel_ai_model_id: this.modelId } : {}),
        test_draft_key: this.draftKey,
      })
      : this.aiAgentService.save({
        name: this.name.trim(),
        smsfunnel_ai_model_id: this.modelId,
        base_prompt: this.prompt.trim(),
        active,
        meta_whatsapp_account_id: this.account?.id || null,
        test_draft_key: this.draftKey,
      } as Partial<AiAgent>);

    this.subscriptions.push(
      request$.subscribe({
        next: saved => {
          this.saving = false;
          const base: Partial<AiAgent> = this.isCopy
            ? { meta_whatsapp_account_id: this.source.meta_whatsapp_account_id ?? null, whatsapp_account: this.source.whatsapp_account ?? null }
            : { meta_whatsapp_account_id: this.account?.id || null };
          this.activeModal.close({ ...base, name: this.name.trim(), base_prompt: this.prompt.trim(), active, ...saved });
        },
        error: res => {
          this.saving = false;
          this.error = res?.error?.message || (this.isCopy ? 'Erro ao copiar o agente. Tente de novo.' : 'Erro ao criar o agente. Tente de novo.');
        },
      })
    );
  }

  private copyName(name: string): string {
    const base = (name || 'Agente').replace(/\s*\(cópia(?:\s+\d+)?\)$/u, '');
    return base.substring(0, AGENT_NAME_MAX - COPY_SUFFIX.length) + COPY_SUFFIX;
  }
}
